// Where the table sits on the page: the pinned header, column locks, the proxy scrollbar and the widths
// they depend on.
//
// The page is the only vertical scroll container, so the header pins with native position: sticky. A
// horizontal scroll container around the table would capture that sticky header, so the region clips
// (overflow-x: clip, which creates no scroll container) and the proxy scrollbar's scrollLeft translates the
// table instead; locked cells translate back by the same amount. Every translation reads one CSS variable on
// the region, written in the proxy's own scroll event, so the header, the locks and the proxy move in the
// same frame. Below 768px none of this applies: the region becomes one native scroll region.

import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { useBreakpoint } from '../useBreakpoint'
import {
  AUTOSCROLL_DEAD_ZONE,
  END_CONTROL,
  START_CONTROL,
  VARS,
  autoscrollSpeed,
  clampScroll,
  lockPlan,
  lockStyleFor,
  maxScroll,
  panIntent,
  releasedLocks,
  revealScroll,
  scrollState,
  wheelDelta,
} from './locks'
import { useContentFloors } from './contentFloor'

/** The proxy scrollbar's track, in CSS pixels. */
export const PROXY_HEIGHT = 16

const REGION = '.me-table-region'
const TABLE = 'table.me-table'

// middle-button autoscroll: the button, the body class that shows the system autoscroll cursor, the fields
// a middle press leaves to the browser, and the longest frame one step may cover
const MIDDLE = 1
export const AUTOSCROLLING = 'me-table-autoscrolling'
const EDITABLE = 'input, textarea, select, [contenteditable]'
const MAX_FRAME_SECONDS = 0.05

// a column's resize boundary and the leading half of its touch grab area (view/ResizeBoundary.js)
const RESIZE_GRAB = '.me-table-resize, .me-table-resize-lead'

// The nearest ancestor that scrolls vertically. The classic app layout scrolls #body_section, not the window.
export const scrollParent = element => {
  for (
    let el = element && element.parentElement;
    el && el !== document.body;
    el = el.parentElement
  ) {
    if (/(auto|scroll)/.test(window.getComputedStyle(el).overflowY)) return el
  }
  return null
}

// Where the pinned header sits. Sticky offsets are measured from the scroll container, so only the part of
// the fixed application header that overlaps the container counts (none when the container starts below it).
export const stickyBoundary = scroller => {
  const containerTop = scroller ? scroller.getBoundingClientRect().top : 0
  const header = document.getElementById('header_navigation_container')
  const fixedBottom =
    header && window.getComputedStyle(header).position === 'fixed'
      ? header.getBoundingClientRect().bottom
      : 0
  return { containerTop, offset: Math.max(0, fixedBottom - containerTop) }
}

const viewportBottom = scroller =>
  scroller
    ? scroller.getBoundingClientRect().top + scroller.clientHeight
    : window.innerHeight

const regionOf = unit => (unit ? unit.querySelector(REGION) : null)

// the instance's region, then every region inside it: a grouped instance gives each section's table a region
// of its own, and each one carries the same scroll position and lock flags
const regionsOf = unit =>
  unit ? Array.from(unit.querySelectorAll(REGION)) : []

const setFlag = (element, name, on) => {
  if (on) element.setAttribute(name, '')
  else element.removeAttribute(name)
}

const sameSize = (a, b) =>
  a.table === b.table &&
  a.full === b.full &&
  a.visible === b.visible &&
  a.content === b.content

// where the start-locked cells end and the end-locked cells begin, in viewport pixels
const lockedBounds = (region, table) => {
  const bounds = region.getBoundingClientRect()
  let clearLeft = bounds.left
  let clearRight = bounds.right
  const row = table.tHead && table.tHead.rows[0]
  if (row)
    Array.from(row.cells).forEach(cell => {
      const rect = cell.getBoundingClientRect()
      if (cell.dataset.lock === 'start' || cell.matches(START_CONTROL))
        clearLeft = Math.max(clearLeft, rect.right)
      else if (cell.dataset.lock === 'end' || cell.matches(END_CONTROL))
        clearRight = Math.min(clearRight, rect.left)
    })
  return { clearLeft, clearRight }
}

/**
 * The header pins below the fixed application header and casts its shadow only while pinned. While the
 * table is wider than its region, columns lock and the proxy scrollbar takes over horizontal scrolling.
 * @param {{current: ?HTMLElement}} unitRef
 * @param {Array<*>} remeasure values whose change moves the table
 * @param {Array<*>} [refloor] values whose change can change a column's content floor (contentFloor.js):
 *   the rows, the summary, the View (density, order, visibility)
 * @returns {import('../types').Geometry}
 */
export const useGeometry = (unitRef, remeasure, refloor = remeasure) => {
  const { narrow } = useBreakpoint()
  const { floors, naturals } = useContentFloors(unitRef, refloor)
  const [pinned, setPinned] = useState(false)
  const [stickyTop, setStickyTop] = useState(0)
  const [size, setSize] = useState({
    table: 0,
    full: 0,
    visible: 0,
    content: 0,
  })
  const [proxyView, setProxyView] = useState({ shown: false, docked: false })
  const [, setGeneration] = useState(0)

  const max = maxScroll(size.table, size.visible)
  const locking = !narrow && max > 0

  const proxyRef = useRef(null)
  const leftRef = useRef(0)
  const live = useRef({ max, locking })
  live.current = { max, locking }

  // the last size measured: measuring runs after every render, and an unchanged size schedules nothing
  const sizeRef = useRef(size)

  // the rendered columns and their widths, registered by the colgroup as it renders, before any header or
  // body cell asks. Locks that leave the scrolling columns less than half the visible width are released for
  // this render (releasedLocks); the region's measured width is the last one this hook read
  const columnsRef = useRef({ columns: [], plan: lockPlan([]) })
  const syncColumns = useCallback((columns, widths) => {
    const measured = sizeRef.current
    const visible = measured.content || measured.visible
    const list = columns || []
    columnsRef.current = {
      columns,
      plan: lockPlan(list, releasedLocks(list, widths, visible)),
    }
  }, [])

  // the spare width the colgroup gave truncated columns this render (ColGroup.js): it only ever fills the
  // region, so overflow is measured without it
  const spareRef = useRef(0)
  const syncSpare = useCallback(total => {
    spareRef.current = total > 0 ? total : 0
  }, [])

  const measure = useCallback(() => {
    const region = regionOf(unitRef.current)
    const table = region && region.querySelector(TABLE)
    let next = { table: 0, full: 0, visible: 0, content: 0 }
    if (table) {
      const style = window.getComputedStyle(region)
      const inset =
        (parseFloat(style.paddingLeft) || 0) +
        (parseFloat(style.paddingRight) || 0)
      next = {
        table: Math.max(0, table.offsetWidth - spareRef.current),
        full: table.offsetWidth,
        visible: region.clientWidth,
        content: Math.max(0, region.clientWidth - inset),
      }
    }
    if (sameSize(sizeRef.current, next)) return
    sizeRef.current = next
    setSize(next)
  }, [unitRef])

  // paints one scroll position: every region's variables and its edge shadows
  const paint = useCallback(
    left => {
      const regions = regionsOf(unitRef.current)
      if (!regions.length) return
      if (!live.current.locking) {
        regions.forEach(region => {
          Object.values(VARS).forEach(name => region.style.removeProperty(name))
          ;['data-shadow-start', 'data-shadow-end'].forEach(name =>
            region.removeAttribute(name)
          )
        })
        return
      }
      const state = scrollState(left, live.current.max)
      regions.forEach(region => {
        Object.entries(state.vars).forEach(([name, value]) =>
          region.style.setProperty(name, value)
        )
        setFlag(region, 'data-shadow-start', state.shadowStart)
        setFlag(region, 'data-shadow-end', state.shadowEnd)
      })
    },
    [unitRef]
  )

  const scrollTo = useCallback(
    left => {
      const next = clampScroll(left, live.current.max)
      leftRef.current = next
      const proxy = proxyRef.current
      if (proxy && proxy.scrollLeft !== next) proxy.scrollLeft = next
      paint(next)
    },
    [paint]
  )

  // every render may have changed widths, visibility or order; reading two widths is cheap
  useLayoutEffect(() => {
    measure()
  })

  // the region and the table resize without a render too (window, container, fonts)
  useLayoutEffect(() => {
    const region = regionOf(unitRef.current)
    if (!region || typeof window.ResizeObserver !== 'function') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }
    const observer = new window.ResizeObserver(() => measure())
    observer.observe(region)
    const table = region.querySelector(TABLE)
    if (table) observer.observe(table)
    return () => observer.disconnect()
  }, [unitRef, measure])

  // lock state on every region: whether it locks, its control edges and the table width the header shadow
  // spans
  useLayoutEffect(() => {
    const regions = regionsOf(unitRef.current)
    if (!regions.length) return
    const { plan } = columnsRef.current
    regions.forEach(region => {
      setFlag(region, 'data-locking', locking)
      setFlag(
        region,
        'data-control-edge-start',
        locking && plan.startEdge === null
      )
      setFlag(region, 'data-control-edge-end', locking && plan.endEdge === null)
      if (size.full > 0) region.style.setProperty(VARS.width, `${size.full}px`)
    })
    if (!locking) leftRef.current = 0
    scrollTo(leftRef.current)
  })

  // the proxy drives the table; its scroll event paints in the same frame
  useLayoutEffect(() => {
    const proxy = proxyRef.current
    if (!locking || !proxy) return undefined
    proxy.scrollLeft = leftRef.current
    const onScroll = () => {
      leftRef.current = clampScroll(proxy.scrollLeft, live.current.max)
      paint(leftRef.current)
    }
    proxy.addEventListener('scroll', onScroll, { passive: true })
    return () => proxy.removeEventListener('scroll', onScroll)
  }, [locking, paint])

  // a horizontal wheel or trackpad gesture over the table, and focus that lands under a locked edge or past
  // the clipped region, scroll through the proxy
  useLayoutEffect(() => {
    const region = regionOf(unitRef.current)
    if (!locking || !region) return undefined
    const onWheel = event => {
      const delta = wheelDelta(event)
      if (!delta) return
      const before = leftRef.current
      scrollTo(before + delta)
      if (leftRef.current !== before) event.preventDefault()
    }
    const onFocus = event => {
      const target = event.target
      if (!target || !target.getBoundingClientRect) return
      // only a table cell scrolls with the table; a grouped section's header sits outside it, so focus
      // there must leave the scroll position alone
      const cell = target.closest('th, td')
      if (!cell || !cell.closest(TABLE)) return
      if (
        cell.dataset.lock ||
        cell.matches(START_CONTROL) ||
        cell.matches(END_CONTROL)
      )
        return
      if (cell.colSpan > 1) return
      const table = region.querySelector(TABLE)
      if (!table) return
      const rect = target.getBoundingClientRect()
      scrollTo(
        revealScroll({
          left: leftRef.current,
          max: live.current.max,
          ...lockedBounds(region, table),
          elementLeft: rect.left,
          elementRight: rect.right,
        })
      )
    }
    region.addEventListener('wheel', onWheel, { passive: false })
    region.addEventListener('focusin', onFocus)
    return () => {
      region.removeEventListener('wheel', onWheel, { passive: false })
      region.removeEventListener('focusin', onFocus)
    }
  }, [locking, unitRef, scrollTo])

  // a touch swipe across the clipped region pans the table; the region's touch-action (pan-y) leaves a
  // vertical swipe to the page, which then cancels the pointer
  useLayoutEffect(() => {
    const region = regionOf(unitRef.current)
    if (!locking || !region) return undefined
    let touch = null
    const onDown = event => {
      if (event.pointerType !== 'touch' || !event.isPrimary) return
      // a touch on a column's resize grab area resizes the column; it never pans the table
      if (
        event.target &&
        event.target.closest &&
        event.target.closest(RESIZE_GRAB)
      )
        return
      touch = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        left: leftRef.current,
        intent: null,
      }
    }
    const onMove = event => {
      if (!touch || event.pointerId !== touch.id) return
      const dx = event.clientX - touch.x
      if (touch.intent === null)
        touch.intent = panIntent(dx, event.clientY - touch.y)
      if (touch.intent === 'horizontal') scrollTo(touch.left - dx)
      else if (touch.intent === 'vertical') touch = null
    }
    const onEnd = event => {
      if (touch && event.pointerId === touch.id) touch = null
    }
    region.addEventListener('pointerdown', onDown)
    region.addEventListener('pointermove', onMove)
    region.addEventListener('pointerup', onEnd)
    region.addEventListener('pointercancel', onEnd)
    return () => {
      region.removeEventListener('pointerdown', onDown)
      region.removeEventListener('pointermove', onMove)
      region.removeEventListener('pointerup', onEnd)
      region.removeEventListener('pointercancel', onEnd)
    }
  }, [locking, unitRef, scrollTo])

  // a middle-button press over the table, then pointer travel, autoscrolls as a browser does on a native
  // scroll region: sideways through the proxy and vertically on the page, faster the further the pointer
  // travels. It ends on release, on any other button press or on Escape. A press that never leaves the dead
  // zone stays a plain middle click, so a link under it still opens in a new tab
  useLayoutEffect(() => {
    const region = regionOf(unitRef.current)
    if (!locking || !region) return undefined
    let drive = null
    let swallowAux = false

    const stop = () => {
      if (!drive) return
      const { moved, frame } = drive
      drive = null
      if (frame) window.cancelAnimationFrame(frame)
      window.removeEventListener('mousemove', onMove, true)
      window.removeEventListener('mouseup', onUp, true)
      window.removeEventListener('mousedown', onOtherPress, true)
      window.removeEventListener('keydown', onKey, true)
      window.removeEventListener('blur', stop)
      document.body.classList.remove(AUTOSCROLLING)
      swallowAux = moved
    }

    const tick = time => {
      if (!drive) return
      const seconds =
        drive.last === null
          ? 0
          : Math.min(MAX_FRAME_SECONDS, (time - drive.last) / 1000)
      drive.last = time
      drive.carryX += autoscrollSpeed(drive.pointerX - drive.x) * seconds
      drive.carryY += autoscrollSpeed(drive.pointerY - drive.y) * seconds
      const stepX = Math.trunc(drive.carryX)
      const stepY = Math.trunc(drive.carryY)
      drive.carryX -= stepX
      drive.carryY -= stepY
      if (stepX) scrollTo(leftRef.current + stepX)
      if (stepY) {
        const scroller = scrollParent(unitRef.current)
        if (scroller) scroller.scrollTop += stepY
        else window.scrollBy(0, stepY)
      }
      drive.frame = window.requestAnimationFrame(tick)
    }

    function onMove(event) {
      if (!drive) return
      drive.pointerX = event.clientX
      drive.pointerY = event.clientY
      if (
        Math.abs(event.clientX - drive.x) > AUTOSCROLL_DEAD_ZONE ||
        Math.abs(event.clientY - drive.y) > AUTOSCROLL_DEAD_ZONE
      )
        drive.moved = true
    }
    function onUp(event) {
      if (event.button === MIDDLE) stop()
    }
    function onOtherPress() {
      stop()
    }
    function onKey(event) {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopPropagation()
      stop()
    }

    const onDown = event => {
      swallowAux = false
      if (event.button !== MIDDLE || drive) return
      if (
        event.target &&
        event.target.closest &&
        event.target.closest(EDITABLE)
      )
        return
      // the browser's own autoscroll would scroll only the page
      event.preventDefault()
      drive = {
        x: event.clientX,
        y: event.clientY,
        pointerX: event.clientX,
        pointerY: event.clientY,
        carryX: 0,
        carryY: 0,
        last: null,
        moved: false,
        frame: window.requestAnimationFrame(tick),
      }
      document.body.classList.add(AUTOSCROLLING)
      window.addEventListener('mousemove', onMove, true)
      window.addEventListener('mouseup', onUp, true)
      window.addEventListener('mousedown', onOtherPress, true)
      window.addEventListener('keydown', onKey, true)
      window.addEventListener('blur', stop)
    }
    // a press that scrolled is not a click: the link under it does not open
    const onAuxClick = event => {
      if (!swallowAux || event.button !== MIDDLE) return
      swallowAux = false
      event.preventDefault()
      event.stopPropagation()
    }

    region.addEventListener('mousedown', onDown)
    region.addEventListener('auxclick', onAuxClick, true)
    return () => {
      stop()
      region.removeEventListener('mousedown', onDown)
      region.removeEventListener('auxclick', onAuxClick, true)
    }
  }, [locking, unitRef, scrollTo])

  // vertical position: the pinned header and where the proxy sits
  useLayoutEffect(() => {
    const scroller = scrollParent(unitRef.current)
    const update = () => {
      const { containerTop, offset } = stickyBoundary(scroller)
      setStickyTop(offset)
      const region = regionOf(unitRef.current)
      const tables = region ? Array.from(region.querySelectorAll(TABLE)) : []
      if (!tables.length || !tables[0].tHead) return
      const boundary = containerTop + offset
      // the one table whose header crosses the sticky boundary pins and casts the pinned shadow; a grouped
      // instance stacks several tables and only the section under the boundary pins
      const pinnedTable = narrow
        ? null
        : tables.find(table => {
            const head = table.tHead
            if (!head) return false
            const rect = table.getBoundingClientRect()
            return (
              rect.top < boundary &&
              rect.bottom > boundary + head.offsetHeight * 2
            )
          }) || null
      tables.forEach(table =>
        setFlag(table, 'data-pinned', table === pinnedTable)
      )
      setPinned(Boolean(pinnedTable))
      const bottom = viewportBottom(scroller)
      const area = region.getBoundingClientRect()
      const shown = area.top < bottom && area.bottom > boundary
      const docked = shown && area.bottom + PROXY_HEIGHT > bottom
      setProxyView(current =>
        current.shown === shown && current.docked === docked
          ? current
          : { shown, docked }
      )
    }
    update()
    let frame = null
    const onChange = () => {
      if (frame) return
      frame = window.requestAnimationFrame(() => {
        frame = null
        update()
      })
    }
    // capture, so a scrolling container (not just the window) reports its scroll
    window.addEventListener('scroll', onChange, {
      passive: true,
      capture: true,
    })
    window.addEventListener('resize', onChange)
    return () => {
      window.removeEventListener('scroll', onChange, { capture: true })
      window.removeEventListener('resize', onChange)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [...remeasure, narrow, locking])

  const invalidate = useCallback(() => {
    measure()
    setGeneration(n => n + 1)
  }, [measure])

  const lockStyle = useCallback(
    columnKey =>
      locking
        ? lockStyleFor(columnsRef.current.plan.sides.get(columnKey) || null)
        : undefined,
    [locking]
  )

  const lockEdges = useCallback(
    columnKey => {
      const { plan } = columnsRef.current
      return {
        start: locking && plan.startEdge === columnKey,
        end: locking && plan.endEdge === columnKey,
      }
    },
    [locking]
  )

  const lockSide = useCallback(
    columnKey =>
      locking ? columnsRef.current.plan.sides.get(columnKey) || null : null,
    [locking]
  )

  return useMemo(
    () => ({
      pinned,
      stickyTop,
      lockStyle,
      lockEdges,
      lockSide,
      invalidate,
      proxyHeight: locking && proxyView.docked ? PROXY_HEIGHT : 0,
      toolbarFreeWidth: 0,
      locking,
      scrollWidth: size.table,
      visibleWidth: size.visible,
      contentWidth: size.content,
      proxyShown: locking && proxyView.shown,
      proxyRef,
      syncColumns,
      syncSpare,
      scrollTo,
      floors,
      naturals,
    }),
    [
      floors,
      naturals,
      pinned,
      stickyTop,
      lockStyle,
      lockEdges,
      lockSide,
      invalidate,
      locking,
      proxyView,
      size,
      syncColumns,
      syncSpare,
      scrollTo,
    ]
  )
}
